/* Stock Report — a port of `Stock Report.dc.html`.
 *
 * The tracker's two stock registers, a month at a time: yarn by count and
 * waste by stream. The design read its rows off a script tag and did the
 * month's arithmetic in the page; this reads the same rows from the API
 * and does the same arithmetic in `stock/model.ts`, which is the design's
 * `renderVals` as it stands. Three things differ: the tabs sit at the top
 * of the page rather than in the header, which belongs to the portal; the
 * Print button is the portal's; and grid lines take the theme's `--c-grid`
 * so the charts survive the dark theme. */

import { useMemo, useState } from 'react'
import { MAIN_STYLE, Subline } from '../App'
import { stock } from '../lib/api_stock'
import type { StockMonth } from '../lib/api_stock'
import { useAsync } from '../lib/useAsync'
import { Empty, ErrorBox, Loading } from '../components/ui'
import { build } from './stock/model'
import type { Controls } from './stock/model'
import { YarnTab } from './stock/YarnTab'
import { WasteTab } from './stock/WasteTab'

type Tab = 'yarn' | 'waste'
const TABS: { k: Tab; l: string }[] = [{ k: 'yarn', l: 'Yarn stock' }, { k: 'waste', l: 'Waste stock' }]

export default function Stock() {
  const [tab, setTab] = useState<Tab>('yarn')
  const [month, setMonth] = useState<string | undefined>(undefined)
  const [S, setS] = useState<Controls>({ asOf: null, vHov: null, wHov: null })
  const set = (patch: Partial<Controls>) => setS((s) => ({ ...s, ...patch }))

  const { data, error, loading, reload } = useAsync(() => stock.summary(month), [month])
  const D: StockMonth | null = data && !data.empty ? data : null
  const v = useMemo(() => (D ? build(D, S) : null), [D, S])

  const pickMonth = (m: string) => {
    setMonth(m)
    set({ asOf: null, vHov: null, wHov: null })
  }

  return (
    <main style={{ ...MAIN_STYLE, opacity: loading && data ? 0.55 : 1, transition: 'opacity 120ms ease' }}>
      <nav className="no-print" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {TABS.map((t) => (
            <button key={t.k} type="button" className={`btn ${tab === t.k ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => { setTab(t.k); set({ vHov: null, wHov: null }) }}>{t.l}</button>
          ))}
        </div>
        {D ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="axis-title">Month</span>
            <select value={D.month} onChange={(e) => pickMonth(e.target.value)} style={{ minWidth: 170 }}>
              {D.months.slice().reverse().map((m) => <option key={m.month} value={m.month}>{m.label}</option>)}
            </select>
          </label>
        ) : null}
      </nav>

      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {loading && !data ? (
        <>
          <Subline>Loading stock registers…</Subline>
          <Loading what="the stock registers" />
        </>
      ) : null}

      {data?.empty ? (
        <>
          <Subline>Yarn and waste stock registers · nothing on file yet</Subline>
          <Empty title="No stock on file yet">
            Load the daily tracker workbook on the Production page's <b>Data files</b> tab
            and both registers fill in.
          </Empty>
        </>
      ) : null}

      {D && v ? (
        <>
          <Subline>{v.periodLine}</Subline>
          {tab === 'yarn' ? (
            <YarnTab v={v} setAsOf={(d) => set({ asOf: d })} setHov={(d) => set({ vHov: d })} />
          ) : (
            <WasteTab v={v} setHov={(d) => set({ wHov: d })} />
          )}
          <footer className="pretty" style={{
            fontSize: 12, color: 'var(--color-neutral-600)', borderTop: '1px solid var(--color-divider)',
            paddingTop: 14, maxWidth: 860,
          }}>{v.footerLine}</footer>
        </>
      ) : null}
    </main>
  )
}
